/**
 * @fileoverview Commencement of bankruptcy (Bankruptcy Act s 115)
 * @module keyDates/bankruptcyCommencement
 *
 * Purpose:
 *  - Compute the commencement of bankruptcy from facts captured on the job
 *    (MOR-1252 / MOR-1253). Commencement anchors the s 120 undervalued
 *    transfer window and the s 122 preference window on a debtor's
 *    petition with a creditor's petition pending; the date of bankruptcy
 *    closes every window.
 *
 * Model:
 *  - The practitioner (or NOA extraction) records the act of bankruptcy
 *    RELIED ON. A creditor's petition states it; a debtor's petition usually
 *    commences on presentation. The s 115 pathways then reduce to a pure
 *    function of that record. There is deliberately no document-scanning
 *    acts-of-bankruptcy engine and no iteration over the s 40(1)(b)
 *    void-transaction limb: a head that finds a void transfer earlier than
 *    commencement flags it for the practitioner to re-anchor.
 *  - s 115(1A) (a bankruptcy-notice extension found frivolous or vexatious)
 *    resolves to "the time that would have applied without the extension".
 *    It is recorded as its own pathway; the arithmetic is the s 115(1)
 *    arithmetic on the earlier date the practitioner enters.
 *  - s 40(4) to (7) deemed dates are applied here (`deemedActDate`): the
 *    practitioner enters the underlying event date and the calculator
 *    derives the deemed commission date.
 *
 * Blocking rule:
 *  - When the pathway cannot be determined the result is `blocked` with a
 *    note naming the missing input. It never defaults to the presentation
 *    date: that silently shortens the s 120 window by up to 6 months.
 *  - A recorded act with no date (`actOfBankruptcyRecorded` without
 *    `actOfBankruptcyDate`) blocks as `incomplete_act` rather than being read
 *    as "no act occurred".
 *  - Chronology that cannot be right (bankruptcy before presentation, an act
 *    or commencement after the date of bankruptcy) blocks as `chronology`;
 *    the update mutation refuses to save it.
 *
 * Used by:
 *  - convex/keyDates/derivations.ts (`deriveRelationBack` bankruptcy tier)
 *  - convex/jobs/mutations.ts (`updateJob` chronology guard)
 *  - convex/jobs/summarization/agents/flaggedTransactions/lib/windows.ts
 *  - src/routes/.../-ui.job-bankruptcy-case-record.tsx (live preview)
 */

import type { Doc } from "@cvx/_generated/dataModel";
import { petitionTypeForAppointmentType } from "@cvx/lib/appointmentTypes";
import type {
  ActOfBankruptcyParagraph,
  BankruptcyPetitionType,
} from "@cvx/jobs/schema";
import {
  addCalendarDays,
  addCalendarMonths,
  formatIsoDate,
  parseIsoDate,
} from "./derivations";

export type BankruptcyCommencementPathway =
  | "s115_1_creditors_petition"
  | "s115_1A_extension_disregarded"
  | "s115_1B_sequestration_order"
  | "s115_2_court_direction"
  | "s115_2_creditors_petition_pending"
  | "s115_2_act_within_six_months"
  | "s115_2_presentation"
  | "legacy_act_of_bankruptcy";

/** Why a result is blocked. `missing_input` is the ordinary incomplete case. */
export type BankruptcyBlockedReason =
  "missing_input" | "incomplete_act" | "chronology";

export interface BankruptcyCommencementInputs {
  petitionType?: BankruptcyPetitionType;
  /** Presentation of the petition, or the s 115(1B) application date. */
  petitionPresentedDate?: string;
  dateOfBankruptcy?: string;
  /**
   * The date entered for the act of bankruptcy. For paragraphs (j), (l),
   * (m) and (n) this is the underlying event; `deemedActDate` derives the
   * date the act is deemed committed.
   */
  actOfBankruptcyDate?: string;
  actOfBankruptcyParagraph?: ActOfBankruptcyParagraph;
  /**
   * True when a paragraph or description is recorded. Distinguishes an act
   * that is recorded but undated (block) from no act recorded at all
   * (s 115(2) item 4 can apply).
   */
  actOfBankruptcyRecorded?: boolean;
  creditorsPetitionPending?: boolean;
  courtSpecifiedCommencementDate?: string;
  /** s 115(1A): the s 41(7) extension was found frivolous, vexatious or without substantial merit. */
  bankruptcyNoticeExtensionDisregarded?: boolean;
  /** Fallbacks for the date of bankruptcy. */
  courtOrderDate?: string;
  appointmentDate?: string;
}

export type DateOfBankruptcyBasis =
  "date_of_bankruptcy" | "court_order" | "appointment";

export interface BankruptcyCommencementResult {
  /** ISO commencement of bankruptcy, or null when blocked. */
  commencement: string | null;
  pathway: BankruptcyCommencementPathway | null;
  /** Citation for display, e.g. "s 115(2) item 3". Null on the legacy path. */
  section: string | null;
  dateOfBankruptcy: string | null;
  dateOfBankruptcyBasis: DateOfBankruptcyBasis | null;
  petitionPresentedDate: string | null;
  blocked: boolean;
  blockedReason: BankruptcyBlockedReason | null;
  /** True on the legacy act-only pathway (petition type never recorded). */
  legacy: boolean;
  /** The s 40 deemed date actually used, when it differs from the entered date. */
  deemedActDate: string | null;
  /** Plain-English derivation, or the reason the result is blocked. */
  note: string;
}

/**
 * Paragraphs of s 40(1) whose act is deemed committed on a date other than
 * the underlying event (s 40(4) to (7)).
 */
export const DEEMED_DATE_PARAGRAPHS: ReadonlyMap<
  ActOfBankruptcyParagraph,
  string
> = new Map<ActOfBankruptcyParagraph, string>([
  ["j", "s 40(4)"],
  ["l", "s 40(5)"],
  ["m", "s 40(6)"],
  ["n", "s 40(7)"],
]);

/** Per-paragraph label for the act-date input where the entered date is an underlying event. */
export const ACT_DATE_LABELS: Partial<
  Record<ActOfBankruptcyParagraph, string>
> = {
  j: "Date the last notice calling the meeting was sent (s 40(4))",
  l: "Date the period to execute the agreement or present the petition expired (s 40(5))",
  m: "Date the personal insolvency agreement was set aside or terminated (s 40(6))",
  n: "Date the composition or scheme was set aside or terminated (s 40(7))",
};

/**
 * s 40(4) to (7) deemed commission dates. Only paragraph (l) needs
 * arithmetic: the act is committed the day AFTER the period expires. The
 * others are deemed committed on the entered event date.
 */
export function deemedActDate(
  paragraph: ActOfBankruptcyParagraph | undefined,
  enteredIso: string,
): string {
  if (paragraph !== "l") return enteredIso;
  const parsed = parseIsoDate(enteredIso);
  if (!parsed) return enteredIso;
  return formatIsoDate(addCalendarDays(parsed, 1));
}

const iso = (raw: string | undefined): string | null => {
  const parsed = parseIsoDate(raw);
  return parsed ? formatIsoDate(parsed) : null;
};

/** `date` has already passed through `iso`, so it parses. */
const sixMonthsBefore = (date: string): string => {
  const parsed = parseIsoDate(date);
  return parsed ? formatIsoDate(addCalendarMonths(parsed, -6)) : date;
};

function resolveDateOfBankruptcy(
  inputs: BankruptcyCommencementInputs,
): Pick<
  BankruptcyCommencementResult,
  "dateOfBankruptcy" | "dateOfBankruptcyBasis"
> {
  const explicit = iso(inputs.dateOfBankruptcy);
  if (explicit) {
    return {
      dateOfBankruptcy: explicit,
      dateOfBankruptcyBasis: "date_of_bankruptcy",
    };
  }
  const order = iso(inputs.courtOrderDate);
  if (order) {
    return { dateOfBankruptcy: order, dateOfBankruptcyBasis: "court_order" };
  }
  const appointment = iso(inputs.appointmentDate);
  if (appointment) {
    return {
      dateOfBankruptcy: appointment,
      dateOfBankruptcyBasis: "appointment",
    };
  }
  return { dateOfBankruptcy: null, dateOfBankruptcyBasis: null };
}

function deemedDateNote(
  paragraph: ActOfBankruptcyParagraph | undefined,
  entered: string,
  deemed: string,
): string {
  if (!paragraph) return "";
  const subsection = DEEMED_DATE_PARAGRAPHS.get(paragraph);
  if (!subsection) return "";
  return deemed === entered
    ? ` Paragraph (${paragraph}) is deemed committed on the date fixed by ${subsection}; the entered date is treated as that deemed date.`
    : ` Paragraph (${paragraph}) is deemed committed the day after the entered date under ${subsection}: entered ${entered}, deemed ${deemed}.`;
}

/**
 * Compute the commencement of bankruptcy under s 115.
 *
 * @example
 * deriveBankruptcyCommencement({
 *   petitionType: "creditors_petition",
 *   petitionPresentedDate: "2026-03-10",
 *   actOfBankruptcyDate: "2026-01-15",
 * }).commencement; // "2026-01-15"
 */
export function deriveBankruptcyCommencement(
  inputs: BankruptcyCommencementInputs,
): BankruptcyCommencementResult {
  const dob = resolveDateOfBankruptcy(inputs);
  const presented = iso(inputs.petitionPresentedDate);
  const enteredAct = iso(inputs.actOfBankruptcyDate);
  const act = enteredAct
    ? deemedActDate(inputs.actOfBankruptcyParagraph, enteredAct)
    : null;
  const deemed = act && enteredAct && act !== enteredAct ? act : null;
  const reminder =
    enteredAct && act
      ? deemedDateNote(inputs.actOfBankruptcyParagraph, enteredAct, act)
      : "";
  const actRecorded = inputs.actOfBankruptcyRecorded === true;

  const base = {
    ...dob,
    petitionPresentedDate: presented,
    deemedActDate: deemed,
  };
  const blocked = (
    note: string,
    reason: BankruptcyBlockedReason = "missing_input",
  ): BankruptcyCommencementResult => ({
    commencement: null,
    pathway: null,
    section: null,
    ...base,
    blocked: true,
    blockedReason: reason,
    legacy: false,
    note,
  });
  const incompleteAct = (): BankruptcyCommencementResult =>
    blocked(
      "An act of bankruptcy is recorded without a date. Enter the date of the act of bankruptcy, or clear the paragraph and description if no act is relied on.",
      "incomplete_act",
    );
  const resolved = (
    commencement: string,
    pathway: BankruptcyCommencementPathway,
    section: string | null,
    note: string,
  ): BankruptcyCommencementResult => {
    // QA-05 chronology: everything must sit on or before the date of bankruptcy.
    const dobIso = dob.dateOfBankruptcy;
    if (dobIso && presented && dobIso < presented) {
      return blocked(
        `The date of bankruptcy (${dobIso}) is before the petition was presented (${presented}). Check the dates.`,
        "chronology",
      );
    }
    if (dobIso && act && act > dobIso) {
      return blocked(
        `The act of bankruptcy relied on (${act}) is after the date of bankruptcy (${dobIso}). Check the dates.`,
        "chronology",
      );
    }
    if (dobIso && commencement > dobIso) {
      return blocked(
        `Commencement (${commencement}) would be after the date of bankruptcy (${dobIso}). Check the dates.`,
        "chronology",
      );
    }
    return {
      commencement,
      pathway,
      section,
      ...base,
      blocked: false,
      blockedReason: null,
      legacy: pathway === "legacy_act_of_bankruptcy",
      note,
    };
  };

  switch (inputs.petitionType) {
    case undefined:
      // Legacy jobs: NOA extraction wrote the act date before the petition
      // type existed. Keep the old anchor so nothing regresses, but say so;
      // the s 120 and s 122 windows stay blocked until the type is set.
      if (act) {
        return resolved(
          act,
          "legacy_act_of_bankruptcy",
          null,
          `Commencement taken as the act of bankruptcy date (${act}) because the petition type has not been recorded. Set the petition type and presentation date so commencement is computed under s 115.`,
        );
      }
      return blocked(
        "Select the petition type and enter the act of bankruptcy relied on to compute commencement of bankruptcy (s 115).",
      );

    case "creditors_petition":
    case "sequestration_order_other": {
      const isOrder = inputs.petitionType === "sequestration_order_other";
      const extension =
        !isOrder && inputs.bankruptcyNoticeExtensionDisregarded === true;
      const anchorLabel = isOrder
        ? "the application for the sequestration order was made"
        : "the petition was presented";
      const citation = isOrder
        ? "s 115(1B)"
        : extension
          ? "s 115(1A)"
          : "s 115(1)";
      const pathway: BankruptcyCommencementPathway = isOrder
        ? "s115_1B_sequestration_order"
        : extension
          ? "s115_1A_extension_disregarded"
          : "s115_1_creditors_petition";
      const extensionNote = extension
        ? " Under s 115(1A) the extension of time to comply with the bankruptcy notice is disregarded, so the act is taken to have been committed when it would have been without the extension; the date entered must be that earlier date."
        : "";
      if (!presented) {
        return blocked(
          `Enter the date ${anchorLabel} to compute commencement of bankruptcy (${citation}).`,
        );
      }
      if (!act && actRecorded) return incompleteAct();
      if (!act) {
        return blocked(
          `Enter the act of bankruptcy relied on. Commencement is the earliest act of bankruptcy in the 6 months before ${anchorLabel} (${presented}); it is never defaulted to that date.`,
        );
      }
      if (act > presented) {
        return blocked(
          `The act of bankruptcy relied on (${act}) is after the petition was presented (${presented}). Check the dates.`,
          "chronology",
        );
      }
      if (act < sixMonthsBefore(presented)) {
        return blocked(
          `The act of bankruptcy relied on (${act}) is more than 6 months before ${anchorLabel} (${presented}). Only an act within that 6 months can found the petition. Check the petition for the act it relies on.`,
        );
      }
      return resolved(
        act,
        pathway,
        citation,
        `Commencement of bankruptcy is ${act}, the act of bankruptcy relied on, being within the 6 months before ${anchorLabel} (${presented}).${extensionNote}${reminder}`,
      );
    }

    case "debtors_petition": {
      const courtTime = iso(inputs.courtSpecifiedCommencementDate);
      if (courtTime) {
        return resolved(
          courtTime,
          "s115_2_court_direction",
          "s 115(2) item 1",
          `Commencement of bankruptcy is ${courtTime}, the time the Court specified when directing acceptance of the debtor's petition.`,
        );
      }
      if (inputs.creditorsPetitionPending === undefined) {
        return blocked(
          "Confirm whether a creditor's petition was pending when the debtor's petition was presented. The answer selects the s 115(2) pathway.",
        );
      }
      if (inputs.creditorsPetitionPending) {
        if (!act && actRecorded) return incompleteAct();
        if (!act) {
          return blocked(
            "A creditor's petition was pending. Enter the earliest act of bankruptcy on which that creditor's petition was based; commencement relates back to it with no 6-month limit (s 115(2) item 2).",
          );
        }
        return resolved(
          act,
          "s115_2_creditors_petition_pending",
          "s 115(2) item 2",
          `Commencement of bankruptcy is ${act}, the earliest act of bankruptcy on which the pending creditor's petition was based. No 6-month limit applies on this pathway.${reminder}`,
        );
      }
      if (!presented) {
        return blocked(
          "Enter the date the debtor's petition was presented to compute commencement of bankruptcy (s 115(2)).",
        );
      }
      if (!act && actRecorded) return incompleteAct();
      const floor = sixMonthsBefore(presented);
      if (act && act <= presented && act >= floor) {
        return resolved(
          act,
          "s115_2_act_within_six_months",
          "s 115(2) item 3",
          `Commencement of bankruptcy is ${act}, an act of bankruptcy within the 6 months before the debtor's petition was presented (${presented}).${reminder}`,
        );
      }
      const staleAct =
        act && act < floor
          ? ` The recorded act of bankruptcy (${act}) is more than 6 months before presentation, so it does not set commencement.`
          : "";
      return resolved(
        presented,
        "s115_2_presentation",
        "s 115(2) item 4",
        `Commencement of bankruptcy is ${presented}, the presentation of the debtor's petition: no creditor's petition was pending and no act of bankruptcy has been recorded in the 6 months before presentation. If an act occurred in that period, record it and commencement will move to it (s 115(2) item 3).${staleAct}`,
      );
    }
  }
}

/** Lift the job scalars the calculator reads. */
export function bankruptcyInputsFromJob(
  job: Pick<
    Doc<"jobs">,
    | "bankruptcyPetitionType"
    | "petitionPresentedDate"
    | "dateOfBankruptcy"
    | "actOfBankruptcyDate"
    | "actOfBankruptcyParagraph"
    | "actOfBankruptcyDescription"
    | "creditorsPetitionPending"
    | "courtSpecifiedCommencementDate"
    | "bankruptcyNoticeExtensionDisregarded"
    | "courtOrderDate"
    | "appointmentDate"
    | "appointmentType"
  >,
): BankruptcyCommencementInputs {
  return {
    // MOR-1838 — a Creditors/Debtors Petition job already says which
    // petition it was; an explicitly recorded petition type still wins.
    petitionType:
      job.bankruptcyPetitionType ??
      petitionTypeForAppointmentType(job.appointmentType),
    petitionPresentedDate: job.petitionPresentedDate,
    dateOfBankruptcy: job.dateOfBankruptcy,
    actOfBankruptcyDate: job.actOfBankruptcyDate,
    actOfBankruptcyParagraph: job.actOfBankruptcyParagraph,
    actOfBankruptcyRecorded:
      job.actOfBankruptcyParagraph !== undefined ||
      (job.actOfBankruptcyDescription ?? "").trim().length > 0,
    creditorsPetitionPending: job.creditorsPetitionPending,
    courtSpecifiedCommencementDate: job.courtSpecifiedCommencementDate,
    bankruptcyNoticeExtensionDisregarded:
      job.bankruptcyNoticeExtensionDisregarded,
    courtOrderDate: job.courtOrderDate,
    appointmentDate: job.appointmentDate,
  };
}
